import { Button, Input, InputNumber, Radio, Space, Switch, Typography } from 'antd';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { TextFilterMode, TextFilterProps, TextFilterValue } from './types';

import s from './TextFilter.module.scss';

const DEFAULT_VALUE: TextFilterValue = { mode: 'text' };

const TextFilter: React.FC<TextFilterProps> = ({
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
  close,
}) => {
  const { t } = useTranslation();
  const currentValue: TextFilterValue = React.useMemo(() => {
    if (selectedKeys.length > 0 && typeof selectedKeys[0] === 'string') {
      try {
        return JSON.parse(selectedKeys[0]) as TextFilterValue;
      } catch {
        return DEFAULT_VALUE;
      }
    }
    return DEFAULT_VALUE;
  }, [selectedKeys]);

  const [mode, setMode] = React.useState<TextFilterMode>(currentValue.mode || 'text');
  const [search, setSearch] = React.useState<string>(currentValue.search || '');
  const [from, setFrom] = React.useState<number | undefined>(currentValue.from);
  const [to, setTo] = React.useState<number | undefined>(currentValue.to);
  const [strict, setStrict] = React.useState<boolean>(currentValue.strict ?? false);

  React.useEffect(() => {
    setMode(currentValue.mode || 'text');
    setSearch(currentValue.search || '');
    setFrom(currentValue.from);
    setTo(currentValue.to);
    setStrict(currentValue.strict ?? false);
  }, [currentValue]);

  const handleApply = React.useCallback(() => {
    const filterValue: TextFilterValue = { mode };

    if (mode === 'text') {
      if (search.trim()) {
        filterValue.search = search.trim();
        setSelectedKeys([JSON.stringify(filterValue)]);
      } else {
        setSelectedKeys([]);
      }
    } else {
      // mode === 'number'
      if (from !== undefined || to !== undefined) {
        if (from !== undefined) filterValue.from = from;
        if (to !== undefined) filterValue.to = to;
        if (strict) filterValue.strict = strict;
        setSelectedKeys([JSON.stringify(filterValue)]);
      } else {
        setSelectedKeys([]);
      }
    }
    
    confirm();
  }, [mode, search, from, to, strict, setSelectedKeys, confirm]);

  const handleReset = React.useCallback(() => {
    setMode('text');
    setSearch('');
    setFrom(undefined);
    setTo(undefined);
    setStrict(false);
    clearFilters?.();
  }, [clearFilters]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleApply();
      }
    },
    [handleApply]
  );

  return (
    <div className={s.textFilter}>
      <div className={s.textFilter__content}>
        <Radio.Group
          value={mode}
          onChange={(e) => setMode(e.target.value)}
          size="small"
          className={s.textFilter__modeSwitch}
        >
          <Radio.Button value="text">{t('filter.text')}</Radio.Button>
          <Radio.Button value="number">{t('filter.number')}</Radio.Button>
        </Radio.Group>

        {mode === 'text' ? (
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('filter.search') as string}
            className={s.textFilter__input}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        ) : (
          <Space direction="vertical" size="small" className={s.textFilter__numberInputs}>
            <div className={s.textFilter__row}>
              <Typography.Text className={s.textFilter__label}>{t('filter.from')}</Typography.Text>
              <InputNumber
                value={from}
                onChange={(value) => setFrom(value ?? undefined)}
                placeholder={t('filter.min') as string}
                className={s.textFilter__numberInput}
              />
            </div>
            
            <div className={s.textFilter__row}>
              <Typography.Text className={s.textFilter__label}>{t('filter.to')}</Typography.Text>
              <InputNumber
                value={to}
                onChange={(value) => setTo(value ?? undefined)}
                placeholder={t('filter.max') as string}
                className={s.textFilter__numberInput}
              />
            </div>

            <div className={s.textFilter__row}>
              <Typography.Text className={s.textFilter__label}>{t('filter.strict')}</Typography.Text>
              <Switch
                checked={strict}
                onChange={setStrict}
                size="small"
              />
              <Typography.Text type="secondary" className={s.textFilter__hint}>
                {strict ? '(> & <)' : '(≥ & ≤)'}
              </Typography.Text>
            </div>
          </Space>
        )}
      </div>

      <Space className={s.textFilter__buttons}>
        <Button size="small" onClick={handleReset}>
          {t('filter.reset')}
        </Button>
        <Button type="primary" size="small" onClick={handleApply}>
          {t('filter.apply')}
        </Button>
      </Space>
    </div>
  );
};

export default TextFilter;
